// This file is auto-generated by @hey-api/openapi-ts

import type { CancelablePromise } from "./core/CancelablePromise";
import { OpenAPI } from "./core/OpenAPI";
import { request as __request } from "./core/request";
import type {
  GetSubjectDetailData,
  GetSubjectDetailResponse,
  GetEducatorModuleDetailsData,
  GetEducatorModuleDetailsResponse,
  GetLiveLessonData,
  GetLiveLessonResponse,
  StartLiveLessonQuizAttemptData,
  StartLiveLessonQuizAttemptResponse,
  SaveLiveLessonQuizAnswerData,
  SaveLiveLessonQuizAnswerResponse,
  SubmitLiveLessonQuizData,
  SubmitLiveLessonQuizResponse,
  UpdateVideoProgressData,
  UpdateVideoProgressResponse,
  GetEducatorTopicInteractionsData,
  GetEducatorTopicInteractionsResponse,
  CreateSavedResourceData,
  CreateSavedResourceResponse,
  ListSavedResourcesData,
  ListSavedResourcesResponse,
  UpdateSavedResourceData,
  UpdateSavedResourceResponse,
  DeleteSavedResourceData,
  DeleteSavedResourceResponse,
  GetEducatorResourceLibraryData,
  GetEducatorResourceLibraryResponse,
  GetEducatorTopicAccessData,
  GetEducatorTopicAccessResponse,
  GetChatHistoryData,
  GetChatHistoryResponse,
  SendMessageData,
  SendMessageResponse,
  CreateChatGrantData,
  CreateChatGrantResponse,
  CreateVoiceGrantData,
  CreateVoiceGrantResponse,
  GetHomepageResponse,
  GetEducatorProfileResponse,
  UpdateEducatorProfileData,
  UpdateEducatorProfileResponse,
  GetEducatorProfileMemoriesResponse,
  UpdateEducatorProfileMemoryData,
  UpdateEducatorProfileMemoryResponse,
  DeleteEducatorProfileMemoryData,
  DeleteEducatorProfileMemoryResponse,
  GetEventsResponse,
  GetEventDetailsData,
  GetEventDetailsResponse,
  GetWebinarsResponse,
  GetAiLabDetailsData,
  GetAiLabDetailsResponse,
  GetAiLabSubmissionData,
  GetAiLabSubmissionResponse,
  CreateAiLabSubmissionData,
  CreateAiLabSubmissionResponse,
  SubmitAiLabSubmissionData,
  SubmitAiLabSubmissionResponse,
  GetAuthUserData,
  GetAuthUserResponse,
  CreateDictationSessionResponse,
  CreateRoleplaySessionData,
  CreateRoleplaySessionResponse,
  GetEducatorCertificateData,
  GetEducatorCertificateResponse,
  ClaimEducatorCertificateData,
  ClaimEducatorCertificateResponse,
  GetPortfolioResponse,
  UpdatePortfolioData,
  UpdatePortfolioResponse,
  CreatePortfolioProjectData,
  CreatePortfolioProjectResponse,
  GetPortfolioProjectData,
  GetPortfolioProjectResponse,
  UpdatePortfolioProjectData,
  UpdatePortfolioProjectResponse,
  CreatePortfolioProjectUploadData,
  CreatePortfolioProjectUploadResponse,
  SubmitPortfolioProjectData,
  SubmitPortfolioProjectResponse,
  CreatePortfolioProjectSuggestionData,
  CreatePortfolioProjectSuggestionResponse,
  GetPublicPortfolioData,
  GetPublicPortfolioResponse,
  GetPublicPortfolioProjectData,
  GetPublicPortfolioProjectResponse,
} from "./types.gen";

export class LearningService {
  /**
   * Get subject detail with modules and progress
   * Returns full subject detail including header info, instructor list,
   * modules with topic thumbnails and progress, and capstone module.
   * Uses Core JWT authentication.
   *
   * @param data The data for the request.
   * @param data.subjectSlug The slug of the learning subject
   * @returns SubjectDetailResponse OK
   * @throws ApiError
   */
  public static getSubjectDetail(
    data: GetSubjectDetailData
  ): CancelablePromise<GetSubjectDetailResponse> {
    return __request(OpenAPI, {
      method: "GET",
      url: "/learning/subjects/{subject_slug}",
      path: {
        subject_slug: data.subjectSlug,
      },
      errors: {
        401: "Unauthorized",
        404: "Not Found",
        500: "Internal Server Error",
      },
    });
  }

  /**
   * Get module details with user progress
   * Get module details for a subject slug, with progress for all modules and topics for the selected module. Uses Core JWT authentication.
   * @param data The data for the request.
   * @param data.subjectSlug The slug of the learning subject
   * @param data.moduleSlug Optional slug to select a specific module. Defaults to the first module if omitted.
   * @returns ModuleDetailsResponse OK
   * @throws ApiError
   */
  public static getEducatorModuleDetails(
    data: GetEducatorModuleDetailsData
  ): CancelablePromise<GetEducatorModuleDetailsResponse> {
    return __request(OpenAPI, {
      method: "GET",
      url: "/learning/subjects/{subject_slug}/modules",
      path: {
        subject_slug: data.subjectSlug,
      },
      query: {
        module_slug: data.moduleSlug,
      },
      errors: {
        401: "Unauthorized",
        404: "Not Found",
        500: "Internal Server Error",
      },
    });
  }

  /**
   * Get a live lesson page
   * Returns everything the live-lesson page renders, one object per step —
   * `intro`, `video`, `quiz`, `results` — plus `current_step`, which says
   * which of them to show. Each step carries its own copy, so the client
   * renders what it is given rather than deciding what to say.
   *
   * A live lesson is a topic taught live once and then delivered as a recording
   * plus a graded quiz. The learner completes it in two steps: a watch
   * requirement (attending the live lesson, or watching enough of the
   * recording), and passing the quiz. Topics with no live lesson return `404`.
   *
   * This is also the reload/resume read — the page rehydrates entirely from
   * this response, so there is no separate transcript or history to replay.
   * While a quiz attempt is in progress the learner's saved answers come back
   * on the questions; once an attempt has been submitted it comes back as
   * `results`.
   *
   * @param data The data for the request.
   * @param data.topicSlug The slug of the topic the live lesson is delivered as
   * @returns LiveLessonResponse OK
   * @throws ApiError
   */
  public static getLiveLesson(
    data: GetLiveLessonData
  ): CancelablePromise<GetLiveLessonResponse> {
    return __request(OpenAPI, {
      method: "GET",
      url: "/learning/live-lessons/{topic_slug}",
      path: {
        topic_slug: data.topicSlug,
      },
      errors: {
        401: "Unauthorized",
        404: "No live lesson exists for this slug — either the topic was not found or it is not delivered as a live lesson.",
      },
    });
  }

  /**
   * Start or retake the quiz
   * Opens a fresh quiz attempt and returns the blank quiz. A learner has a
   * single active attempt at a time: if one is already in progress it is
   * returned unchanged, so the call is safe to repeat. After a failed
   * submission this opens a new attempt with no saved answers, starting at the
   * first question; earlier attempts are kept but never pre-filled into the new
   * one.
   *
   * Rejected with `quiz_locked` while the watch requirement is unmet, and with
   * `quiz_passed` once the learner has passed — a passed quiz is locked into
   * read-only review and cannot be retaken.
   *
   * @param data The data for the request.
   * @param data.topicSlug The slug of the topic the live lesson is delivered as
   * @returns LiveLessonQuiz OK — the blank quiz for the active attempt.
   * @throws ApiError
   */
  public static startLiveLessonQuizAttempt(
    data: StartLiveLessonQuizAttemptData
  ): CancelablePromise<StartLiveLessonQuizAttemptResponse> {
    return __request(OpenAPI, {
      method: "POST",
      url: "/learning/live-lessons/{topic_slug}/quiz/attempt",
      path: {
        topic_slug: data.topicSlug,
      },
      errors: {
        400: "The `topic_slug` is missing, does not match any topic, or is not delivered as a live lesson.",
        401: "Unauthorized",
        404: "The topic is delivered as a live lesson, but no published lesson exists for it, the lesson has no quiz, or the learner has no progress recorded for the lesson.",
        409: "The quiz cannot be started in its current state (`quiz_locked`, `quiz_passed`). See `LiveLessonErrorCode`.",
      },
    });
  }

  /**
   * Save the learner's answer to one quiz question
   * Saves (or replaces) the learner's selected choice for a single question on
   * the active attempt, so the quiz can be resumed where they left off. Answers
   * are saved as the learner goes; nothing is graded here and no correctness is
   * revealed, which is why the call returns no body. Grading happens only on
   * submit.
   *
   * Rejected with `quiz_locked` while the watch requirement is unmet, and with
   * `quiz_passed` once the learner has passed — a passed quiz is locked and
   * becomes read-only review.
   *
   * @param data The data for the request.
   * @param data.topicSlug The slug of the topic the live lesson is delivered as
   * @param data.requestBody
   * @returns void No Content — the answer was saved.
   * @throws ApiError
   */
  public static saveLiveLessonQuizAnswer(
    data: SaveLiveLessonQuizAnswerData
  ): CancelablePromise<SaveLiveLessonQuizAnswerResponse> {
    return __request(OpenAPI, {
      method: "PUT",
      url: "/learning/live-lessons/{topic_slug}/quiz/answer",
      path: {
        topic_slug: data.topicSlug,
      },
      body: data.requestBody,
      mediaType: "application/json",
      errors: {
        401: "Unauthorized",
        404: "No live lesson exists for this slug — either the topic was not found or it is not delivered as a live lesson.",
        409: "The quiz cannot be answered in its current state (`quiz_locked`, `quiz_passed`). See `LiveLessonErrorCode`.",
        422: "The question or the choice is not part of this quiz (`invalid_answer`). See `LiveLessonErrorCode`.",
      },
    });
  }

  /**
   * Submit the quiz for grading
   * Grades the answers saved on the active attempt and returns the `results` —
   * the same object `GET /learning/live-lessones/{topic_slug}` returns once an
   * attempt has been submitted. The request takes no body: calling this
   * endpoint is the submit intent, and the answers were already saved through
   * `PUT /learning/live-lessones/{topic_slug}/quiz/answer`.
   *
   * Grading decides what the results screen says, so the copy and the CTA come
   * back on this response rather than being derived client-side. The graded
   * outcome is persisted, so a later page load returns the same `results`.
   *
   * Every question must be answered; an incomplete quiz is rejected with
   * `quiz_incomplete` and nothing is graded. Submitting is rejected with
   * `quiz_locked` while the watch requirement is unmet.
   *
   * A failing submission leaves the lesson open for a retake. A passing
   * submission completes the lesson and locks the quiz into read-only review;
   * submitting again returns that same passing `results` so the learner can
   * review their score.
   *
   * @param data The data for the request.
   * @param data.topicSlug The slug of the topic the live lesson is delivered as
   * @returns LiveLessonResults OK — the graded results.
   * @throws ApiError
   */
  public static submitLiveLessonQuiz(
    data: SubmitLiveLessonQuizData
  ): CancelablePromise<SubmitLiveLessonQuizResponse> {
    return __request(OpenAPI, {
      method: "POST",
      url: "/learning/live-lessons/{topic_slug}/quiz/submit",
      path: {
        topic_slug: data.topicSlug,
      },
      errors: {
        401: "Unauthorized",
        404: "No live lesson exists for this slug — either the topic was not found or it is not delivered as a live lesson.",
        409: "The quiz cannot be submitted in its current state (`quiz_locked`, `quiz_incomplete`). See `LiveLessonErrorCode`.",
      },
    });
  }

  /**
   * Report the learner's position in a video
   * Records the learner's current position (`cursor`) in a video. The server
   * keeps a separate high-water mark (`progress`) that only moves forward: a
   * cursor beyond it advances it, a cursor behind it (the learner scrubbing
   * back or rewatching) leaves it untouched. A cursor past the end of the
   * video is clamped to its duration. The call is therefore idempotent
   * and safe to send repeatedly and in any order — once when the client reaches
   * a threshold, or periodically as a heartbeat.
   *
   * Returns both positions, the video length, and the fraction watched
   * (computed from `progress`). Any completion rule layered on top of a video —
   * for example a live lesson's watch requirement — is evaluated against
   * `progress` by that resource, not here.
   *
   * @param data The data for the request.
   * @param data.videoId Identifier of the video resource
   * @param data.requestBody
   * @returns VideoProgress OK — the stored watch position.
   * @throws ApiError
   */
  public static updateVideoProgress(
    data: UpdateVideoProgressData
  ): CancelablePromise<UpdateVideoProgressResponse> {
    return __request(OpenAPI, {
      method: "PUT",
      url: "/learning/videos/{video_id}/progress",
      path: {
        video_id: data.videoId,
      },
      body: data.requestBody,
      mediaType: "application/json",
      errors: {
        401: "Unauthorized",
        404: "No video exists for this id.",
        422: "The reported position is not usable — negative.",
      },
    });
  }

  /**
   * Get historical interaction data and module structure for a topic
   * Returns interaction data for a topic, grouped by sub-collection.
   * Sub-collections today are `artifacts` (historical conversation
   * artifacts), `saved_resources` (bot messages the learner saved — whole
   * or as text highlights — each with an optional note) and `modules`
   * (the subject/module/topic structure and progress for the subject this
   * topic belongs to — the same payload as `getEducatorModuleDetails`).
   * Future historical types will land as new optional keys in the same
   * response — no new endpoints. Uses Core JWT authentication.
   *
   * The `modules` sub-collection lets clients render module navigation
   * from this single call instead of a separate modules request. It
   * contains the structure for the topic's own module.
   *
   * @param data The data for the request.
   * @param data.topicSlug The slug of the learning topic.
   * @returns TopicInteractionsResponse OK
   * @throws ApiError
   */
  public static getEducatorTopicInteractions(
    data: GetEducatorTopicInteractionsData
  ): CancelablePromise<GetEducatorTopicInteractionsResponse> {
    return __request(OpenAPI, {
      method: "GET",
      url: "/learning/topics/{topic_slug}/interactions",
      path: {
        topic_slug: data.topicSlug,
      },
      errors: {
        401: "Unauthorized",
        403: "Forbidden",
        404: "Not Found",
        500: "Internal Server Error",
      },
    });
  }

  /**
   * Save a resource on a topic
   * Saves a resource for the current user on the given topic, with an
   * optional plain-text note attached. Exactly one anchor must be provided:
   *
   * - `message_uuid` — save a whole bot modality message (video, image,
   * longform article, or shocking fact). Each resource can be saved once
   * per learner; saving it again is rejected with `409`.
   * - `ranges` — save highlighted text from bot messages (see
   * `HighlightRange`; offsets are client-defined and stored opaquely).
   * Any bot message may carry highlights — standalone text, the copy
   * beside a modality (including the text above a longform article),
   * table cells. Which passages are selectable is the client's concern:
   * it never offers the paginated article BODY (a highlight there can't
   * restore its page). A selection may span multiple messages — the
   * client decomposes it into per-message sub-ranges, skipping learner
   * messages. Highlights must not overlap another of the learner's
   * saved highlights on the topic.
   *
   * The server enforces, as relaxable validations (`422`), that a
   * whole-save targets a whole-saveable bot modality message and a
   * highlight targets the learner's own bot messages.
   *
   * Service-layer obligations (deliberately no schema/model/DB backstop —
   * the implementing service owns these): the exactly-one-anchor rule
   * (schema `oneOf` was skipped because the Ruby generator emits awkward
   * wrappers; note that both-anchors-in-agreement would otherwise persist
   * as a valid text save), message existence (`404`), and the
   * bot/modality/text/own-conversation eligibility rules. The model and DB
   * backstop everything else: note cap, range well-formedness, non-overlap,
   * whole-save-once.
   *
   * Returns `201` with the created `SavedResource` — the same shape
   * `GET /learning/topics/{topic_slug}/interactions` lists — so the client
   * can address the new save (note edits, unsave) without a refetch.
   * Resources can be saved at any time — mid-lesson or after completion.
   *
   * @param data The data for the request.
   * @param data.topicSlug The slug of the learning topic.
   * @param data.requestBody
   * @returns SavedResource Created — the new saved resource.
   * @throws ApiError
   */
  public static createSavedResource(
    data: CreateSavedResourceData
  ): CancelablePromise<CreateSavedResourceResponse> {
    return __request(OpenAPI, {
      method: "POST",
      url: "/learning/topics/{topic_slug}/saved-resources",
      path: {
        topic_slug: data.topicSlug,
      },
      body: data.requestBody,
      mediaType: "application/json",
      errors: {
        401: "Unauthorized",
        404: "The topic, or a referenced message, was not found. Message lookups are scoped to the learner's own bot messages, so a learner-message or foreign-conversation uuid surfaces here, not as a 422.",
        409: "The resource is already saved by this learner.",
        422: "Invalid save: both or neither anchor provided, note over the length cap, malformed range (bad offsets, blank quote), whole-saving anything but a whole-saveable modality, or a highlight overlapping another of the learner's saved highlights on this topic.",
      },
    });
  }

  /**
   * List the learner's saved resources across a subject
   * Returns every resource the current user saved across the given
   * subject's topics, grouped by topic in curriculum order — module
   * order, then topic order within the module. Within each group, items
   * are transcript-ordered and shaped exactly as the `saved_resources`
   * collection of `GET /learning/topics/{topic_slug}/interactions`; each
   * group adds the topic and module context the Saved page needs to
   * render module sections and per-card topic badges.
   *
   * Topics the learner saved nothing on are omitted — clients render the
   * full module list (including save-less modules) from the
   * module-structure endpoints, keyed by each group's `module.slug`.
   * Saves whose topic has been removed from the curriculum are excluded.
   *
   * The response is not paginated: the subject scope and the per-topic
   * save cap keep the payload bounded. `subject_slug` is required today
   * because the Saved page is always scoped to one subject; if an
   * all-subjects view ships, the plan is to relax the parameter to
   * optional rather than change the response shape.
   *
   * @param data The data for the request.
   * @param data.subjectSlug Slug of the subject whose saves to list.
   * @returns SavedResourcesListResponse OK
   * @throws ApiError
   */
  public static listSavedResources(
    data: ListSavedResourcesData
  ): CancelablePromise<ListSavedResourcesResponse> {
    return __request(OpenAPI, {
      method: "GET",
      url: "/learning/saved-resources",
      query: {
        subject_slug: data.subjectSlug,
      },
      errors: {
        401: "Unauthorized",
        404: "The subject was not found, or is not available to this learner.",
      },
    });
  }

  /**
   * Set or clear the note on a saved resource
   * Replaces the note attached to one of the current user's saves; a blank
   * string clears it. The save's anchor — the message or highlight
   * ranges — is fixed at creation; re-highlighting is delete + recreate.
   * Returns `204` with an empty body — clients refetch the topic
   * interactions for the updated collection.
   *
   * @param data The data for the request.
   * @param data.savedResourceUuid UUID of the save (from `SavedResource.uuid`).
   * @param data.requestBody
   * @returns void Updated — no body.
   * @throws ApiError
   */
  public static updateSavedResource(
    data: UpdateSavedResourceData
  ): CancelablePromise<UpdateSavedResourceResponse> {
    return __request(OpenAPI, {
      method: "PATCH",
      url: "/learning/saved-resources/{saved_resource_uuid}",
      path: {
        saved_resource_uuid: data.savedResourceUuid,
      },
      body: data.requestBody,
      mediaType: "application/json",
      errors: {
        401: "Unauthorized",
        404: "The save was not found (including saves owned by another user).",
        422: "Invalid note (over the length cap).",
      },
    });
  }

  /**
   * Unsave a resource
   * Removes one of the current user's saves — the save and its attached
   * note together.
   *
   * @param data The data for the request.
   * @param data.savedResourceUuid UUID of the save (from `SavedResource.uuid`).
   * @returns void Deleted — no content.
   * @throws ApiError
   */
  public static deleteSavedResource(
    data: DeleteSavedResourceData
  ): CancelablePromise<DeleteSavedResourceResponse> {
    return __request(OpenAPI, {
      method: "DELETE",
      url: "/learning/saved-resources/{saved_resource_uuid}",
      path: {
        saved_resource_uuid: data.savedResourceUuid,
      },
      errors: {
        401: "Unauthorized",
        404: "The save was not found (including saves owned by another user).",
      },
    });
  }

  /**
   * Browse a subject's resource library
   * Returns the learner's resource catalog for one subject, grouped by
   * module and topic.
   *
   * @param data The data for the request.
   * @param data.subjectSlug Subject to browse. Defaults to the subject of the learner's most recent lesson, or to the first subject in the list when they have not started one.
   * @param data.moduleSlug Module to browse. Defaults to the subject's first module.
   * @param data.filter Modality filter applied to the returned resources.
   * @returns ResourceLibraryResponse OK
   * @throws ApiError
   */
  public static getEducatorResourceLibrary(
    data: GetEducatorResourceLibraryData = {}
  ): CancelablePromise<GetEducatorResourceLibraryResponse> {
    return __request(OpenAPI, {
      method: "GET",
      url: "/learning/library",
      query: {
        subject_slug: data.subjectSlug,
        module_slug: data.moduleSlug,
        filter: data.filter,
      },
      errors: {
        401: "Unauthorized",
        404: "The subject or module was not found, or is not available to this learner.",
      },
    });
  }

  /**
   * Decide whether the user can render the requested topic, or must be redirected to a dependency.
   * Walks the topic's single-level dependency configuration. Returns either a
   * resolved decision (the user may render the requested topic) or a redirect
   * target (the user must complete the dependency topic first). Returns 500
   * when the dependency configuration is invalid for this iteration's rules
   * (server-side CMS data error — caller cannot recover, ops must fix).
   *
   * @param data The data for the request.
   * @param data.subjectSlug Slug of the learning subject containing the requested topic.
   * @param data.moduleSlug Slug of the module containing the requested topic.
   * @param data.topicSlug Slug of the topic the user is requesting access to.
   * @returns EducatorTopicAccessResponse OK
   * @throws ApiError
   */
  public static getEducatorTopicAccess(
    data: GetEducatorTopicAccessData
  ): CancelablePromise<GetEducatorTopicAccessResponse> {
    return __request(OpenAPI, {
      method: "GET",
      url: "/learning/subjects/{subject_slug}/modules/{module_slug}/topics/{topic_slug}/access",
      path: {
        subject_slug: data.subjectSlug,
        module_slug: data.moduleSlug,
        topic_slug: data.topicSlug,
      },
      errors: {
        401: "Unauthorized",
        404: "Subject, module, or topic not found.",
        500: "Dependency configuration is invalid for this iteration's rules (server-side CMS data error).",
      },
    });
  }
}

export class ChatHistoryService {
  /**
   * Get chat history
   * @param data The data for the request.
   * @param data.subjectSlug Subject slug
   * @param data.moduleSlug Module slug
   * @param data.topicSlug Topic slug to filter messages by
   * @param data.cursor
   * @param data.limit
   * @param data.anchorMessageUuid UUID of a message to anchor the window on. When provided, the response contains a
   * window of messages centered on the anchor plus both next_cursor (older) and
   * previous_cursor (newer). Mutually exclusive with `cursor` (returns 422 if both provided).
   *
   * @returns Dialog OK
   * @throws ApiError
   */
  public static getChatHistory(
    data: GetChatHistoryData
  ): CancelablePromise<GetChatHistoryResponse> {
    return __request(OpenAPI, {
      method: "GET",
      url: "/learning/subjects/{subject_slug}/modules/{module_slug}/chat-history",
      path: {
        subject_slug: data.subjectSlug,
        module_slug: data.moduleSlug,
      },
      query: {
        topic_slug: data.topicSlug,
        cursor: data.cursor,
        limit: data.limit,
        anchor_message_uuid: data.anchorMessageUuid,
      },
      errors: {
        400: "Bad Request",
        401: "Unauthorized",
        404: "Not Found",
      },
    });
  }
}

export class MessagesService {
  /**
   * Send a message to a bot
   * Send a message to a bot
   * @param data The data for the request.
   * @param data.subjectSlug Subject slug
   * @param data.moduleSlug Module slug
   * @param data.requestBody
   * @returns string The request was successful
   * @throws ApiError
   */
  public static sendMessage(
    data: SendMessageData
  ): CancelablePromise<SendMessageResponse> {
    return __request(OpenAPI, {
      method: "POST",
      url: "/learning/subjects/{subject_slug}/modules/{module_slug}/dialogs/messages",
      path: {
        subject_slug: data.subjectSlug,
        module_slug: data.moduleSlug,
      },
      body: data.requestBody,
      mediaType: "application/json",
      errors: {
        400: "Bad Request",
        401: "Unauthorized",
        404: "Not Found",
      },
    });
  }
}

export class ChatGrantService {
  /**
   * Mint a chat grant for direct-cortex streaming
   * Called once per chat session (screen mount) and cached until near expiry;
   * re-fetched on expiry or a cortex 401
   * (docs/proposals/jarvis-direct-cortex-streaming.md, Phase 2). Authenticates
   * and entitles the user, finds-or-creates the module's dialogue, and returns
   * the short-lived JWT the client uses to talk to cortex directly. No
   * analytics field: the grant is transport plumbing, never rendered.
   *
   * @param data The data for the request.
   * @param data.subjectSlug Subject the chat belongs to.
   * @param data.moduleSlug Module the chat belongs to.
   * @returns ChatGrantResponse Grant minted
   * @throws ApiError
   */
  public static createChatGrant(
    data: CreateChatGrantData
  ): CancelablePromise<CreateChatGrantResponse> {
    return __request(OpenAPI, {
      method: "POST",
      url: "/learning/subjects/{subject_slug}/modules/{module_slug}/chat-grant",
      path: {
        subject_slug: data.subjectSlug,
        module_slug: data.moduleSlug,
      },
      errors: {
        401: "Authentication credentials are missing or invalid.",
        404: "Subject or module not found, or not available to this user.",
      },
    });
  }

  /**
   * Mint a voice grant for the Jarvis voice WebSocket
   * Same checks and dialogue as the chat grant, but the token carries the
   * jarvis-voice audience and authorizes only the cortex voice WebSocket
   * (stream_url), passed there as the `token` query param.
   *
   * @param data The data for the request.
   * @param data.subjectSlug Subject the chat belongs to.
   * @param data.moduleSlug Module the chat belongs to.
   * @param data.resetTopicSlug Test-only. Before minting, clears this learner's plan, attempts,
   * saved resources and transcript for this topic of the module, so the
   * voice session starts it from scratch. Allowed where the Jarvis
   * fixtures API is (development, preview apps, and allowlisted learners
   * on staging); refused with 403 everywhere else, including production.
   *
   * @returns ChatGrantResponse Grant minted
   * @throws ApiError
   */
  public static createVoiceGrant(
    data: CreateVoiceGrantData
  ): CancelablePromise<CreateVoiceGrantResponse> {
    return __request(OpenAPI, {
      method: "POST",
      url: "/learning/subjects/{subject_slug}/modules/{module_slug}/voice-grant",
      path: {
        subject_slug: data.subjectSlug,
        module_slug: data.moduleSlug,
      },
      query: {
        reset_topic_slug: data.resetTopicSlug,
      },
      errors: {
        401: "Authentication credentials are missing or invalid.",
        403: "reset_topic_slug was sent where topic resets are not allowed.",
        404: "Subject, module or reset topic not found, or not available to this user.",
      },
    });
  }
}

export class EducatorHomepageService {
  /**
   * Get Educator Homepage data
   * Returns aggregated Educator Homepage data, including upcoming events,
   * the full list of subjects, and the current subject with its associated topics.
   *
   * @returns HomepageResponse OK
   * @throws ApiError
   */
  public static getHomepage(): CancelablePromise<GetHomepageResponse> {
    return __request(OpenAPI, {
      method: "GET",
      url: "/homepage",
      errors: {
        401: "Unauthorized",
        500: "Internal Server Error",
      },
    });
  }
}

export class EducatorProfileService {
  /**
   * Get educator profile tab data
   * Returns the Profile tab data for the Educator Profile modal.
   *
   * This is a BFF endpoint — all display values are pre-formatted and
   * the client renders them as-is. Labels, placeholders, and CTA texts
   * are returned by the server.
   *
   * This is the default tab in the modal. Its response also provides the
   * data needed to render the shared Boogie ID card header displayed
   * across all profile tabs.
   *
   * Fields that have not been set yet are returned as null in `text.value`.
   *
   * @returns EducatorProfileResponse OK
   * @throws ApiError
   */
  public static getEducatorProfile(): CancelablePromise<GetEducatorProfileResponse> {
    return __request(OpenAPI, {
      method: "GET",
      url: "/profile",
      errors: {
        401: "Unauthorized",
      },
    });
  }

  /**
   * Update educator profile fields
   * Partially updates the educator profile fields.
   *
   * All body fields are optional — only provided fields are persisted.
   * Called after each onboarding chat answer and when the user edits
   * and saves the Profile tab manually.
   *
   * Returns the full updated BFF profile response on success.
   *
   * @param data The data for the request.
   * @param data.requestBody
   * @returns EducatorProfileResponse OK
   * @throws ApiError
   */
  public static updateEducatorProfile(
    data: UpdateEducatorProfileData
  ): CancelablePromise<UpdateEducatorProfileResponse> {
    return __request(OpenAPI, {
      method: "PATCH",
      url: "/profile",
      body: data.requestBody,
      mediaType: "application/json",
      errors: {
        401: "Unauthorized",
        422: "Unprocessable Entity",
      },
    });
  }

  /**
   * Get the learner's memories for the Profile card
   * Returns everything the Memory section of the Profile card renders:
   * what we have learned about the learner from their conversations, and
   * the teaching instructions they have given.
   *
   * This is a BFF endpoint — section titles, group labels and CTA text are
   * returned by the server and rendered as-is.
   *
   * Scope and shaping, all applied server-side:
   *
   * - Only `personal` and `preference` memories are returned. `progress` and
   * `performance` memories are the model's assessments of the learner
   * rather than facts a learner would recognise and correct, and are never
   * exposed here.
   * - Preferences are limited to the five levers the tutor can actually
   * enact. See `MemoryGroupKey`.
   * - Sections and groups with no memories are omitted entirely, so the
   * client never has to render an empty shell.
   * - `text.value` is written in the second person, addressed to the learner.
   * Where a generated rendering is not yet available the server falls back
   * to a deterministic transform of the stored text, so this is never blank
   * and the client never sees the third-person form the model reads.
   *
   * @returns EducatorMemoriesResponse OK
   * @throws ApiError
   */
  public static getEducatorProfileMemories(): CancelablePromise<GetEducatorProfileMemoriesResponse> {
    return __request(OpenAPI, {
      method: "GET",
      url: "/profile/memories",
      errors: {
        401: "Authentication credentials are missing or invalid.",
      },
    });
  }

  /**
   * Correct a memory
   * Replaces the wording of a single memory with the learner's correction.
   *
   * The submitted text is not stored as given. It is normalised back to the
   * third person the tutor reads, and anything that is not a fact about the
   * learner is refused — so instruction-shaped text cannot reach the tutor's
   * prompt through this endpoint.
   *
   * The response deliberately carries no display text. The client keeps
   * showing the learner their own wording; the stored version appears the next
   * time the card is opened.
   *
   * @param data The data for the request.
   * @param data.memoryToken Opaque identifier for a single memory, from `Memory.memory_token`.
   * @param data.requestBody
   * @returns void Stored.
   * @throws ApiError
   */
  public static updateEducatorProfileMemory(
    data: UpdateEducatorProfileMemoryData
  ): CancelablePromise<UpdateEducatorProfileMemoryResponse> {
    return __request(OpenAPI, {
      method: "PATCH",
      url: "/profile/memories/{memory_token}",
      path: {
        memory_token: data.memoryToken,
      },
      body: data.requestBody,
      mediaType: "application/json",
      errors: {
        401: "Authentication credentials are missing or invalid.",
        404: `No such memory for this learner. A token that is valid but belongs to
someone else is deliberately indistinguishable from one that does not
exist, and so is any request made while the feature is switched off.
`,
        422: `The correction could not be stored as a memory (\`edit_rejected\`) — it
carried no fact about the learner, or was refused as unsafe to keep.
The message is fixed copy; the reason stays server-side. See
\`MemoryEditErrorCode\`.
`,
      },
    });
  }

  /**
   * Delete a memory
   * Removes a memory from the learner's Profile card and from everything the
   * tutor reads.
   *
   * The row is soft-deleted and marked as removed by the learner, so a
   * deletion stays distinguishable from a memory that consolidation absorbed.
   *
   * Deleting does not stop an equivalent fact being learned again: if the
   * learner raises the same thing in a later lesson, extraction may write a
   * new memory. This is a correction, not a suppression list.
   *
   * @param data The data for the request.
   * @param data.memoryToken Opaque identifier for a single memory, from `Memory.memory_token`.
   * @returns void Deleted.
   * @throws ApiError
   */
  public static deleteEducatorProfileMemory(
    data: DeleteEducatorProfileMemoryData
  ): CancelablePromise<DeleteEducatorProfileMemoryResponse> {
    return __request(OpenAPI, {
      method: "DELETE",
      url: "/profile/memories/{memory_token}",
      path: {
        memory_token: data.memoryToken,
      },
      errors: {
        401: "Authentication credentials are missing or invalid.",
        404: `No such memory for this learner. A token that is valid but belongs to
someone else is deliberately indistinguishable from one that does not
exist, and so is any request made while the feature is switched off.
`,
      },
    });
  }
}

export class EducatorEventsService {
  /**
   * Get Educator Events page data
   * Returns the Educator Events page data organized by tabs.
   * The "AI Labs" tab includes a hero event and a list of events.
   * The "Events" tab includes a list of hero events.
   *
   * @returns EducatorEventsPageResponse OK
   * @throws ApiError
   */
  public static getEvents(): CancelablePromise<GetEventsResponse> {
    return __request(OpenAPI, {
      method: "GET",
      url: "/events",
      errors: {
        401: "Unauthorized",
        500: "Internal Server Error",
      },
    });
  }

  /**
   * Get Educator event detail page data
   * Returns the content for a single event's detail page: the event
   * recording, the display copy rendered around it, and the page images.
   *
   * @param data The data for the request.
   * @param data.slug URL-friendly unique identifier of the event
   * @returns EducatorEventDetailsResponse OK
   * @throws ApiError
   */
  public static getEventDetails(
    data: GetEventDetailsData
  ): CancelablePromise<GetEventDetailsResponse> {
    return __request(OpenAPI, {
      method: "GET",
      url: "/events/{slug}",
      path: {
        slug: data.slug,
      },
      errors: {
        401: "Unauthorized",
        404: "Not Found",
        500: "Internal Server Error",
      },
    });
  }
}

export class EducatorWebinarsService {
  /**
   * Get eligible upcoming webinars for the current learner
   * Returns the webinars the current learner is eligible for — those open to
   * all learners, plus any targeted at the learner's cohort — that are still
   * upcoming (start time in the future), in chronological order with the
   * soonest webinar first. Past webinars are never returned.
   *
   * @returns EducatorWebinarListResponse OK
   * @throws ApiError
   */
  public static getWebinars(): CancelablePromise<GetWebinarsResponse> {
    return __request(OpenAPI, {
      method: "GET",
      url: "/webinars",
      errors: {
        401: "Unauthorized",
        500: "Internal Server Error",
      },
    });
  }
}

export class EducatorAiLabsService {
  /**
   * Get AI Lab landing page data
   * Returns all content for the AI Lab landing page: banner, hosts,
   * details, guidelines, project submission, and sample submission.
   *
   * @param data The data for the request.
   * @param data.slug URL-friendly unique identifier of the AI Lab
   * @returns AiLabDetailsResponse OK
   * @throws ApiError
   */
  public static getAiLabDetails(
    data: GetAiLabDetailsData
  ): CancelablePromise<GetAiLabDetailsResponse> {
    return __request(OpenAPI, {
      method: "GET",
      url: "/ai-labs/{slug}",
      path: {
        slug: data.slug,
      },
      errors: {
        401: "Unauthorized",
        404: "Not Found",
        500: "Internal Server Error",
      },
    });
  }
}

export class EducatorSubmissionsService {
  /**
   * Get the student's AI Lab submission
   * Returns the current user's submission for the given AI Lab: status,
   * uploaded-file info (including the file's async processing `status`),
   * status-driven display text, and — once the submission has been reviewed
   * (`approved` / `revision_requested`) — read-only rubric feedback.
   *
   * A lab the student has not started yet still returns a submission object,
   * with `status: not_started`. See `AiLabSubmissionStatus` for the full
   * lifecycle; the file's `status` (see `AiLabSubmissionFileStatus`)
   * transitions asynchronously after the direct upload completes — re-fetch
   * this endpoint to observe it.
   *
   * @param data The data for the request.
   * @param data.slug URL-friendly unique identifier of the AI Lab.
   * @returns AiLabSubmissionResponse OK
   * @throws ApiError
   */
  public static getAiLabSubmission(
    data: GetAiLabSubmissionData
  ): CancelablePromise<GetAiLabSubmissionResponse> {
    return __request(OpenAPI, {
      method: "GET",
      url: "/ai-labs/{slug}/submission",
      path: {
        slug: data.slug,
      },
      errors: {
        401: "Unauthorized",
        404: "The AI Lab was not found.",
      },
    });
  }

  /**
   * Create an AI Lab submission and mint upload credentials
   * Creates a draft submission bound to the current user and lab, and returns
   * it (`submission`: `uuid` + `status`) alongside signed Filestack upload
   * `credentials`. The client uploads the video bytes directly to Filestack
   * with these credentials, tagging the upload with the submission's `uuid`
   * and the mint's attempt discriminator
   * (`tags: {submission_id, upload_attempt}`). No follow-up call is needed
   * after the upload: the stored file is linked to the submission
   * server-side — file metadata (filename, content type) included — and the
   * file's async `status` (see `AiLabSubmissionFileStatus`) is observable on
   * `GET /ai-labs/{slug}/submission`. The request takes no body.
   *
   * Calling this again before the previous upload resolved (e.g. after the
   * client cancelled it) supersedes that upload: the new mint's tags carry a
   * fresh attempt discriminator, and a late callback from the superseded
   * upload is ignored and its file discarded. The newest mint always wins.
   *
   * Rejected with `submission_locked` when the lab has not opened for
   * submissions yet (`status: locked`), or `already_submitted` once the
   * submission has advanced to `under_review` or beyond.
   *
   * Upload constraints (max size, allowed content types, single file) are
   * enforced client-side at launch and are not modeled by this contract.
   *
   * @param data The data for the request.
   * @param data.slug URL-friendly unique identifier of the AI Lab.
   * @returns AiLabSubmissionCreateResponse OK — the draft submission plus signed upload credentials.
   * @throws ApiError
   */
  public static createAiLabSubmission(
    data: CreateAiLabSubmissionData
  ): CancelablePromise<CreateAiLabSubmissionResponse> {
    return __request(OpenAPI, {
      method: "POST",
      url: "/ai-labs/{slug}/submission",
      path: {
        slug: data.slug,
      },
      errors: {
        401: "Unauthorized",
        404: "The AI Lab was not found.",
        409: "The submission cannot be created in its current state (`submission_locked`, `already_submitted`). See `AiLabSubmissionErrorCode`.",
      },
    });
  }

  /**
   * Submit an AI Lab draft for review
   * Advances the draft submission to `under_review`. Calling this endpoint is
   * itself the submit-for-review intent; the body is optional and carries only
   * the learner's partner-share consent, so a request with no body records no
   * consent and submits normally. Submit is
   * gated on the uploaded file having finished processing and cleared (its
   * `status` is reviewer-ready); a draft whose file is still processing or
   * failed is rejected with `file_not_ready` and stays a draft, to be retried
   * once the file resolves. This is a one-way transition: once submitted, the
   * submission is locked against further client edits and cannot be withdrawn —
   * attempts return `already_submitted` (or `submission_locked` before the lab
   * opens).
   *
   * Resubmission after `revision_requested` reuses the same
   * create → upload → submit path; there is no separate resubmit endpoint and
   * no cap on the number of resubmissions.
   *
   * @param data The data for the request.
   * @param data.slug URL-friendly unique identifier of the AI Lab.
   * @param data.requestBody
   * @returns AiLabSubmissionResponse OK — the submission, now in `under_review` status.
   * @throws ApiError
   */
  public static submitAiLabSubmission(
    data: SubmitAiLabSubmissionData
  ): CancelablePromise<SubmitAiLabSubmissionResponse> {
    return __request(OpenAPI, {
      method: "POST",
      url: "/ai-labs/{slug}/submission/submit",
      path: {
        slug: data.slug,
      },
      body: data.requestBody,
      mediaType: "application/json",
      errors: {
        401: "Unauthorized",
        404: "There is no draft submission to submit (`submission_not_found`). See `AiLabSubmissionErrorCode`.",
        409: "The submission cannot be submitted in its current state (`submission_locked`, `already_submitted`, `file_not_ready`). See `AiLabSubmissionErrorCode`.",
      },
    });
  }
}

export class AuthService {
  /**
   * Get user details
   * Get the details of a user by UUID. This endpoint will return the user's details, including their accessible products.
   * A user can only access their own details.
   *
   * @param data The data for the request.
   * @param data.uuid The UUID of the user, or "me" to fetch the current user.
   *
   * @returns User OK
   * @throws ApiError
   */
  public static getAuthUser(
    data: GetAuthUserData
  ): CancelablePromise<GetAuthUserResponse> {
    return __request(OpenAPI, {
      method: "GET",
      url: "/auth/users/{uuid}",
      path: {
        uuid: data.uuid,
      },
      errors: {
        401: `Unauthorized
The user is not authenticated.
`,
        404: "Not Found",
      },
    });
  }
}

export class DictationService {
  /**
   * Mint a dictation session token
   * Mints a short-lived, user-scoped JWT that the browser presents when
   * opening the Cortex dictation WebSocket. The token is audience-scoped
   * to dictation and carries no conversation context.
   *
   * @returns DictationSessionResponse OK
   * @throws ApiError
   */
  public static createDictationSession(): CancelablePromise<CreateDictationSessionResponse> {
    return __request(OpenAPI, {
      method: "POST",
      url: "/dictation/sessions",
      errors: {
        401: `Unauthorized
The user is not authenticated.
`,
        403: `Forbidden
The user has no educator grant and, while applicant dictation
is enabled, is not an applicant with the essay step open.
`,
      },
    });
  }
}

export class RoleplayService {
  /**
   * Start a voice roleplay session
   * Creates a Parley conversation for the voice roleplay message the learner is
   * starting and mints the token the browser presents when opening the Cortex
   * Parley audio WebSocket. Each call creates a new conversation, so starting the
   * roleplay again is another call. No analytics field: the session is transport
   * plumbing, never rendered.
   *
   * @param data The data for the request.
   * @param data.requestBody
   * @returns RoleplaySessionResponse Session created
   * @throws ApiError
   */
  public static createRoleplaySession(
    data: CreateRoleplaySessionData
  ): CancelablePromise<CreateRoleplaySessionResponse> {
    return __request(OpenAPI, {
      method: "POST",
      url: "/roleplay/sessions",
      body: data.requestBody,
      mediaType: "application/json",
      errors: {
        401: "Authentication credentials are missing or invalid.",
        403: "The user has no educator grant.",
        404: "The roleplay message was not found, or does not belong to this user.",
        422: "The message is not a voice roleplay, or it can no longer be started.",
      },
    });
  }
}

export class EducatorCertificateService {
  /**
   * Get the learner's certificate state for a program
   * The only certificate read. Serves the homepage notification, the Certificate
   * tab in the profile modal, and the poll that runs after a claim.
   *
   * The server resolves which of three states the learner is in and ships the
   * images and the CTAs for that state. Display copy is hardcoded client-side
   * per variant; the strings here are CTA labels and urls.
   *
   * Screen variants:
   * - `locked`: the learner has not met the program requirements. No
   * certificate image, no CTAs.
   * - `pending`: the requirements are met but the credential does not exist
   * yet. Carries the muted preview image.
   * - `issued`: the credential is available. Carries the certificate image,
   * the download, the copy link and the LinkedIn share.
   * - `error`: issuing or reading the credential against BC Diploma failed.
   * Terminal. All errors are treated the same for now; the client renders its
   * own copy. This is a 200 (an expected product state), not a 5xx.
   *
   * Eligibility is not a fourth state: a learner who has met the requirements
   * but has never claimed is `pending`, so the variant spans both sides of
   * the claim. `ctas.claim` is what separates them — present while the claim
   * is still open, null once a reward row exists and issuance is in flight.
   * See `ctas`.
   *
   * @param data The data for the request.
   * @param data.programUuid The unique identifier of the educator program whose certificate is being read. The uuid is the cross-service identifier for a program — core keys `educator_programs` on a bigint internally and exposes this uuid outward.
   * @returns EducatorCertificateResponse Certificate state retrieved successfully
   * @throws ApiError
   */
  public static getEducatorCertificate(
    data: GetEducatorCertificateData
  ): CancelablePromise<GetEducatorCertificateResponse> {
    return __request(OpenAPI, {
      method: "GET",
      url: "/programs/{program_uuid}/certificate",
      path: {
        program_uuid: data.programUuid,
      },
      errors: {
        401: "Unauthorized - User is not authenticated",
        404: "Not Found - The program does not exist, or the authenticated learner is not enrolled in it.",
        500: "Internal Server Error",
      },
    });
  }

  /**
   * Claim the learner's certificate for a program
   * Claims the certificate, creating the reward row and starting issuance.
   * Returns `202` with no body — it does not wait for the credential to
   * exist.
   *
   * The body carries the learner's Boogie badge SVG to store on the credential.
   *
   * Certificate state is read from the GET on this path, never from the
   * claim. A successful claim leaves the learner in `pending` with `ctas`
   * now null, so the client polls the GET until the variant becomes
   * `issued`.
   *
   * Offered only when the GET returned `ctas.claim`. Claiming when a reward
   * row already exists is a no-op.
   *
   * @param data The data for the request.
   * @param data.programUuid The unique identifier of the educator program whose certificate is being claimed. The uuid is the cross-service identifier for a program — core keys `educator_programs` on a bigint internally and exposes this uuid outward.
   * @param data.requestBody
   * @returns unknown Accepted - the claim was recorded and issuance has started. No body; the learner's certificate state is read from the GET on this path.
   * @throws ApiError
   */
  public static claimEducatorCertificate(
    data: ClaimEducatorCertificateData
  ): CancelablePromise<ClaimEducatorCertificateResponse> {
    return __request(OpenAPI, {
      method: "POST",
      url: "/programs/{program_uuid}/certificate",
      path: {
        program_uuid: data.programUuid,
      },
      body: data.requestBody,
      mediaType: "application/json",
      errors: {
        401: "Unauthorized - User is not authenticated",
        404: "Not Found - The program does not exist, or the authenticated learner is not enrolled in it.",
        422: "Unprocessable Entity - The learner has not met the program requirements, so there is nothing to claim, or `badge_svg` is missing or blank.",
        500: "Internal Server Error",
      },
    });
  }
}

export class EducatorPortfolioService {
  /**
   * Get the caller's portfolio
   * Returns the authenticated user's portfolio, owner view, with all of its active projects ordered by position.
   * @returns PortfolioPage OK
   * @throws ApiError
   */
  public static getPortfolio(): CancelablePromise<GetPortfolioResponse> {
    return __request(OpenAPI, {
      method: "GET",
      url: "/portfolio",
      errors: {
        401: "Authentication credentials are missing or invalid.",
        404: "Not found or not accessible.",
      },
    });
  }

  /**
   * Update the caller's portfolio
   * Updates the authenticated user's portfolio settings. Only the fields sent are changed.
   * Making the portfolio public without a first and last name returns `422 name_required`.
   * A `layout_mode` outside `PortfolioLayoutMode` returns `422 invalid_fields`.
   *
   * @param data The data for the request.
   * @param data.requestBody
   * @returns PortfolioPage OK
   * @throws ApiError
   */
  public static updatePortfolio(
    data: UpdatePortfolioData
  ): CancelablePromise<UpdatePortfolioResponse> {
    return __request(OpenAPI, {
      method: "PATCH",
      url: "/portfolio",
      body: data.requestBody,
      mediaType: "application/json",
      errors: {
        401: "Authentication credentials are missing or invalid.",
        404: "Not found or not accessible.",
        422: "Invalid fields.",
      },
    });
  }

  /**
   * Get or create the caller's portfolio project for an AI Lab
   * Returns the authenticated user's active portfolio project for the given AI Lab, creating it as a `draft` when there is none.
   * There is at most one active project per AI Lab, so repeating the call returns the same project.
   * The caller's portfolio is created first when it does not exist yet.
   * Responds `201` when the project was created and `200` when it already existed.
   * Returns the project only: it opens no AI Lab submission and mints no upload credentials. The file upload goes through `POST /portfolio/projects/{project_key}/upload` when the learner picks a file.
   *
   * @param data The data for the request.
   * @param data.requestBody
   * @returns PortfolioProjectPage The project already existed
   * @throws ApiError
   */
  public static createPortfolioProject(
    data: CreatePortfolioProjectData
  ): CancelablePromise<CreatePortfolioProjectResponse> {
    return __request(OpenAPI, {
      method: "POST",
      url: "/portfolio/projects",
      body: data.requestBody,
      mediaType: "application/json",
      errors: {
        401: "Authentication credentials are missing or invalid.",
        404: "Not found or not accessible.",
        422: "Invalid fields.",
      },
    });
  }

  /**
   * Get one of the caller's portfolio projects
   * Returns a project from the authenticated user's portfolio by uuid or slug, owner view.
   * @param data The data for the request.
   * @param data.projectKey Project uuid or slug. A `draft` project has no slug yet, so clients address it by uuid.
   * Slugs are for URLs only; clients use the uuid whenever they have it.
   *
   * @returns PortfolioProjectPage OK
   * @throws ApiError
   */
  public static getPortfolioProject(
    data: GetPortfolioProjectData
  ): CancelablePromise<GetPortfolioProjectResponse> {
    return __request(OpenAPI, {
      method: "GET",
      url: "/portfolio/projects/{project_key}",
      path: {
        project_key: data.projectKey,
      },
      errors: {
        401: "Authentication credentials are missing or invalid.",
        404: "Not found or not accessible.",
      },
    });
  }

  /**
   * Update one of the caller's portfolio projects
   * Updates a project from the authenticated user's portfolio by uuid or slug. Only the fields sent are changed.
   * Making a project public before its source is approved returns `422 project_not_approved`.
   * The slug is regenerated when the title changes; the response carries the new `redirect_url`.
   *
   * @param data The data for the request.
   * @param data.projectKey Project uuid or slug. A `draft` project has no slug yet, so clients address it by uuid.
   * Slugs are for URLs only; clients use the uuid whenever they have it.
   *
   * @param data.requestBody
   * @returns PortfolioProjectPage OK
   * @throws ApiError
   */
  public static updatePortfolioProject(
    data: UpdatePortfolioProjectData
  ): CancelablePromise<UpdatePortfolioProjectResponse> {
    return __request(OpenAPI, {
      method: "PATCH",
      url: "/portfolio/projects/{project_key}",
      path: {
        project_key: data.projectKey,
      },
      body: data.requestBody,
      mediaType: "application/json",
      errors: {
        401: "Authentication credentials are missing or invalid.",
        404: "Not found or not accessible.",
        422: "Invalid fields.",
      },
    });
  }

  /**
   * Open the project's AI Lab draft and mint upload credentials
   * Portfolio path for `POST /ai-labs/{slug}/submission`, keyed on one of the caller's portfolio projects.
   * It opens or reuses the learner's draft attempt for the project's AI Lab and returns the same body:
   * the draft `submission` and signed Filestack upload `credentials`. Call it when the learner picks a
   * file, never earlier: the credentials expire and the mint starts the upload processing window.
   * The request takes no body.
   *
   * A project that does not exist or is not in the caller's portfolio returns `404`.
   *
   * @param data The data for the request.
   * @param data.projectKey Project uuid or slug. A `draft` project has no slug yet, so clients address it by uuid.
   * Slugs are for URLs only; clients use the uuid whenever they have it.
   *
   * @returns AiLabSubmissionCreateResponse OK — the draft submission plus signed upload credentials.
   * @throws ApiError
   */
  public static createPortfolioProjectUpload(
    data: CreatePortfolioProjectUploadData
  ): CancelablePromise<CreatePortfolioProjectUploadResponse> {
    return __request(OpenAPI, {
      method: "POST",
      url: "/portfolio/projects/{project_key}/upload",
      path: {
        project_key: data.projectKey,
      },
      errors: {
        401: "Authentication credentials are missing or invalid.",
        404: "Not found or not accessible.",
        409: "The submission cannot be created in its current state (`submission_locked`, `already_submitted`). See `AiLabSubmissionErrorCode`.",
      },
    });
  }

  /**
   * Submit one of the caller's portfolio projects
   * Portfolio path for `POST /ai-labs/{slug}/submission/submit`, keyed on one of the caller's portfolio projects.
   * It submits the learner's current draft attempt for the project's AI Lab under the same rules, and moves the project out of `draft`.
   * The body is optional and carries only the learner's share consent; a request with no body records no consent and submits normally.
   *
   * The project's title and description must both be set: a draft may hold them blank, but the slug is derived from the title,
   * so a blank one returns `422 invalid_fields` and nothing is submitted.
   * AI Lab submission errors reach the client unchanged: `409` carries the `AiLabSubmissionErrorCode` values, and a project
   * with no draft attempt to submit returns `404 submission_not_found`.
   * A project that does not exist or is not in the caller's portfolio returns `404 not_found`.
   *
   * @param data The data for the request.
   * @param data.projectKey Project uuid or slug. A `draft` project has no slug yet, so clients address it by uuid.
   * Slugs are for URLs only; clients use the uuid whenever they have it.
   *
   * @param data.requestBody
   * @returns PortfolioProjectPage OK — the project, now out of `draft`
   * @throws ApiError
   */
  public static submitPortfolioProject(
    data: SubmitPortfolioProjectData
  ): CancelablePromise<SubmitPortfolioProjectResponse> {
    return __request(OpenAPI, {
      method: "POST",
      url: "/portfolio/projects/{project_key}/submit",
      path: {
        project_key: data.projectKey,
      },
      body: data.requestBody,
      mediaType: "application/json",
      errors: {
        401: "Authentication credentials are missing or invalid.",
        404: "The project does not exist or is not in the caller's portfolio (`not_found`), or it has no draft attempt to submit (`submission_not_found`, see `AiLabSubmissionErrorCode`).",
        409: "The AI Lab submission cannot be submitted in its current state (`submission_locked`, `file_not_ready`, `already_submitted`). See `AiLabSubmissionErrorCode`.",
        422: "Invalid fields.",
      },
    });
  }

  /**
   * Generate a new AI suggestion for one of the caller's portfolio projects
   * Starts generating a new title and description suggestion from the transcript of the project's linked AI Lab submission.
   * The request takes no body. Generation is asynchronous: the call returns `202` with no body, and the client polls
   * `GET /portfolio/projects/{project_key}` and reads the result from `project.suggestion`.
   *
   * Returns `409 transcript_not_ready` when the linked submission has no transcript yet, the same condition that makes
   * `suggestion.can_generate` false.
   * A project that does not exist or is not in the caller's portfolio returns `404`.
   *
   * @param data The data for the request.
   * @param data.projectKey Project uuid or slug. A `draft` project has no slug yet, so clients address it by uuid.
   * Slugs are for URLs only; clients use the uuid whenever they have it.
   *
   * @returns unknown Accepted — generation has started. Poll the project GET for the result
   * @throws ApiError
   */
  public static createPortfolioProjectSuggestion(
    data: CreatePortfolioProjectSuggestionData
  ): CancelablePromise<CreatePortfolioProjectSuggestionResponse> {
    return __request(OpenAPI, {
      method: "POST",
      url: "/portfolio/projects/{project_key}/suggestion",
      path: {
        project_key: data.projectKey,
      },
      errors: {
        401: "Authentication credentials are missing or invalid.",
        404: "Not found or not accessible.",
        409: "The linked AI Lab submission has no transcript yet (`transcript_not_ready`). See `PortfolioErrorCode`.",
      },
    });
  }

  /**
   * Get a portfolio by handle
   * Returns a portfolio, visitor view, with its public projects ordered by position.
   * Returns `404` unless the portfolio is public or the caller owns it.
   *
   * @param data The data for the request.
   * @param data.handle Public portfolio handle, matched case-insensitively
   * @returns PublicPortfolioPage OK
   * @throws ApiError
   */
  public static getPublicPortfolio(
    data: GetPublicPortfolioData
  ): CancelablePromise<GetPublicPortfolioResponse> {
    return __request(OpenAPI, {
      method: "GET",
      url: "/portfolios/{handle}",
      path: {
        handle: data.handle,
      },
      errors: {
        404: "Not found or not accessible.",
      },
    });
  }

  /**
   * Get a portfolio project by handle and slug
   * Returns a public project of a portfolio, visitor view.
   * Returns `404` unless the project is public and the portfolio is public or owned by the caller.
   *
   * @param data The data for the request.
   * @param data.handle Public portfolio handle, matched case-insensitively
   * @param data.projectSlug URL-friendly portfolio project identifier
   * @returns PublicPortfolioProjectPage OK
   * @throws ApiError
   */
  public static getPublicPortfolioProject(
    data: GetPublicPortfolioProjectData
  ): CancelablePromise<GetPublicPortfolioProjectResponse> {
    return __request(OpenAPI, {
      method: "GET",
      url: "/portfolios/{handle}/projects/{project_slug}",
      path: {
        handle: data.handle,
        project_slug: data.projectSlug,
      },
      errors: {
        404: "Not found or not accessible.",
      },
    });
  }
}
